import { useMemo } from 'react';
import type { SkillChip } from '@/components/core/skill-chips/skill-chips';
import { skillCategoryOf } from '../components/employee-profile-view/profile-cards';
import {
  PROFICIENCY_LEVELS,
  resolveSubCategoryId,
  storedSkillName,
  toSkillValues,
  toSubCategoryExperienceValues,
} from '../schemas/skills-schema';
import type { Employee } from '../types/employee.types';
import { useEmploymentTypes } from './use-employees';
import { useGetSkillsFor, useGetSkillTaxonomy } from './use-skill-lookups';

/** Names the People list shows for its Employee Type, Skills, Category and Sub-Category columns. */
export interface EmployeeListLookups {
  skillsOf: (employee: Employee) => SkillChip[];
  categoryOf: (employee: Employee) => string;
  /** The sub-categories the person works in now (no end date), joined. */
  subCategoriesOf: (employee: Employee) => string;
  employmentTypeOf: (employee: Employee) => string;
  /** What the Category and Sub-Category header filters offer (the ids are what the profile stores). */
  categoryOptions: { value: string; label: string }[];
  subCategoryOptions: { value: string; label: string }[];
}

/**
 * Reads, once per page, what the list's profile columns need beyond the rows themselves: the skill
 * taxonomy (cached), the names of the page's skills and each person's current employment type (their
 * HR term; only for viewers allowed to read it, others see "—").
 */
export const useEmployeeListLookups = (employees: Employee[]): EmployeeListLookups => {
  const { data: taxonomy } = useGetSkillTaxonomy();
  const skillIds = useMemo(
    () => [
      ...new Set(
        employees.flatMap((employee) => toSkillValues(employee.Skills).map((s) => s.Skill))
      ),
    ],
    [employees]
  );
  const { data: skills } = useGetSkillsFor(skillIds, [], skillIds.length > 0);
  const userIds = useMemo(
    () => employees.map((employee) => employee.UserId ?? '').filter(Boolean),
    [employees]
  );
  const { data: employmentTypes } = useEmploymentTypes(userIds);

  return useMemo(() => {
    const subCategories = taxonomy?.subCategories ?? [];
    const titleOfSubCategory = (id?: string | null) =>
      subCategories.find((item) => item.ItemId === id)?.Title;

    return {
      categoryOptions: (taxonomy?.categories ?? []).map((item) => ({
        value: item.ItemId,
        label: item.Title,
      })),
      subCategoryOptions: subCategories.map((item) => ({ value: item.ItemId, label: item.Title })),
      skillsOf: (employee) =>
        (employee.Skills ?? []).map((entry) => {
          const skill = skills?.find((item) => item.ItemId === entry.Skill);
          const subCategory = titleOfSubCategory(skill?.SubCategoryId ?? entry.SubCategoryId);
          return {
            name: skill?.Name ?? storedSkillName(entry),
            tone: skillCategoryOf(subCategory),
            subCategory,
            level: PROFICIENCY_LEVELS.find((level) => level.key === entry.Proficiency)?.title,
          };
        }),
      categoryOf: (employee) =>
        employee.CategoryId
          ? (taxonomy?.categories.find((item) => item.ItemId === employee.CategoryId)?.Title ?? '')
          : '',
      subCategoriesOf: (employee) =>
        toSubCategoryExperienceValues(employee.SubCategoryExperiences)
          .filter((entry) => entry.IsUntilNow || !entry.EndDate)
          .map(
            (entry) =>
              titleOfSubCategory(resolveSubCategoryId(entry.SubCategoryId, subCategories)) ??
              entry.SubCategoryId
          )
          .join(', '),
      employmentTypeOf: (employee) => employmentTypes?.[employee.UserId ?? ''] ?? '',
    };
  }, [taxonomy, skills, employmentTypes]);
};
